import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import * as bookmark from './core/bookmark';
import { EditorHistory, HistoryKind } from './core/history';
import { applyLink, autolinkBefore, linkAt, removeLink } from './core/links';
import { activeList, isEmptyListItem, outdentEmptyItem, toggleList } from './core/lists';
import { clearFormatting, hasMarkThroughout, toggleMark } from './core/marks';
import { normalize } from './core/normalize';
import { parseExternalHtml, parseValue, plainTextToFragment } from './core/parse-html';
import { serialize } from './core/serialize';

export type EditorCommand =
  | 'bold' | 'italic' | 'ul' | 'ol' | 'link' | 'unlink' | 'clear' | 'undo' | 'redo';

/** What the template renders. Recomputed only when something actually changed. */
interface ToolbarState {
  bold: boolean;
  italic: boolean;
  ul: boolean;
  ol: boolean;
  link: boolean;
  canUndo: boolean;
  canRedo: boolean;
  empty: boolean;
}

/** Input types that would introduce tags outside the frozen schema. */
const FORBIDDEN_INPUT_TYPES: ReadonlySet<string> = new Set([
  'formatUnderline',
  'formatStrikeThrough',
  'formatSuperscript',
  'formatSubscript',
  'formatFontColor',
  'formatFontName',
  'formatBackColor',
  'formatJustifyLeft',
  'formatJustifyCenter',
  'formatJustifyRight',
  'formatJustifyFull',
  'formatIndent',
  'formatOutdent',
]);

let instanceCounter = 0;

@Component({
  selector: 'brt-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true,
    },
  ],
})
export class RichTextEditorComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
  @ViewChild('content', { static: true }) contentRef!: ElementRef<HTMLDivElement>;
  @ViewChild('linkInput') linkInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('linkBubble') linkBubbleRef?: ElementRef<HTMLElement>;

  @Input('placeholder') placeholder = 'Hier Text eingeben ...';
  @Input('has-error') hasError = false;
  @Input('label-id') labelId = '';
  @Input('aria-label') ariaLabel = '';
  @Input('readonly') readonly = false;

  @Input('value') set value(incoming: string | null) {
    this.writeValue(incoming);
  }

  @Input('disabled') set disabledInput(isDisabled: boolean) {
    this.setDisabledState(isDisabled);
  }

  @Output('onChange') changed = new EventEmitter<string>();
  @Output('onBlur') blurred = new EventEmitter<void>();

  readonly contentId = `brt-rte-content-${++instanceCounter}`;

  disabled = false;
  state: ToolbarState = {
    bold: false, italic: false, ul: false, ol: false, link: false,
    canUndo: false, canRedo: false, empty: true,
  };

  linkBubbleOpen = false;
  linkDraft = '';
  linkInvalid = false;
  linkBubbleTop = 0;
  linkBubbleLeft = 0;
  linkBubbleEditing = false;

  /** Index of the toolbar button that owns the single tab stop. */
  activeButton = 0;

  private readonly history = new EditorHistory();
  private readonly listeners: Array<() => void> = [];

  private viewReady = false;
  private pendingValue: string | null = null;
  private lastEmitted = '';
  private lastSelection: bookmark.Bookmark | null = null;
  /** Selection the link bubble was opened against, restored before applying. */
  private linkTarget: bookmark.Bookmark | null = null;
  private isFocused = false;
  private isComposing = false;
  private suppressProcessing = false;
  private forcePlainTextPaste = false;
  private frame = 0;

  private onChangeFn: (value: string) => void = () => undefined;
  private onTouchedFn: () => void = () => undefined;

  constructor(
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
  ) {}

  private get root(): HTMLDivElement {
    return this.contentRef.nativeElement;
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.applyValue(this.pendingValue ?? '');
    this.pendingValue = null;

    this.zone.runOutsideAngular(() => {
      this.on(this.root, 'beforeinput', (event) => this.onBeforeInput(event as InputEvent));
      this.on(this.root, 'input', (event) => this.onInput(event as InputEvent));
      this.on(this.root, 'keydown', (event) => this.onKeyDown(event as KeyboardEvent));
      this.on(this.root, 'paste', (event) => this.onPaste(event as ClipboardEvent));
      this.on(this.root, 'focus', () => this.onFocus());
      this.on(this.root, 'blur', () => this.onBlur());
      this.on(this.root, 'compositionstart', () => { this.isComposing = true; });
      this.on(this.root, 'compositionend', () => {
        this.isComposing = false;
        // Android commits the DOM after the event, so let it settle first.
        window.setTimeout(() => this.process('type'), 0);
      });
      this.on(document, 'selectionchange', () => this.onSelectionChange());
      this.on(document, 'pointerdown', (event) => this.onDocumentPointerDown(event));
    });
  }

  ngOnDestroy(): void {
    for (const off of this.listeners) {
      off();
    }
    this.listeners.length = 0;
    if (this.frame) {
      cancelAnimationFrame(this.frame);
    }
    this.history.clear();
  }

  // --- ControlValueAccessor -------------------------------------------------

  writeValue(incoming: string | null): void {
    const next = incoming ?? '';

    if (!this.viewReady) {
      this.pendingValue = next;
      return;
    }

    // The five profile and job fields autosave on a 1500ms debounce and push the
    // value straight back. Rewriting the DOM under a live caret would eat every
    // character typed during that window, so an echo is dropped.
    if (this.isFocused || next === this.lastEmitted) {
      return;
    }

    this.applyValue(next);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  // --- Commands -------------------------------------------------------------

  run(command: EditorCommand): void {
    if (this.disabled || this.readonly) {
      return;
    }

    if (command === 'undo' || command === 'redo') {
      this.applyHistory(command);
      return;
    }

    if (command === 'link') {
      if (this.linkBubbleOpen) {
        this.closeLinkBubble();
      } else {
        this.openLinkBubble();
      }
      return;
    }

    const range = this.currentRange();
    if (!range) {
      return;
    }

    switch (command) {
      case 'bold':
        toggleMark(this.root, range, 'B');
        break;
      case 'italic':
        toggleMark(this.root, range, 'I');
        break;
      case 'ul':
        toggleList(this.root, range, 'UL');
        break;
      case 'ol':
        toggleList(this.root, range, 'OL');
        break;
      case 'clear':
        clearFormatting(this.root, range);
        break;
      case 'unlink':
        removeLink(this.root, range);
        break;
    }

    this.process('command');
  }

  confirmLink(): void {
    // Focus currently sits in the bubble's input, so the document selection is
    // no longer inside the editor. Put it back before touching the document.
    const range = this.reclaimSelection();
    if (!range) {
      this.linkInvalid = true;
      this.cdr.markForCheck();
      return;
    }

    if (!applyLink(this.root, range, this.linkDraft)) {
      this.linkInvalid = true;
      this.cdr.markForCheck();
      return;
    }

    this.linkBubbleOpen = false;
    this.linkInvalid = false;
    this.cdr.markForCheck();
    this.process('command');
  }

  onBubbleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      // Must be prevented: confirmLink() focuses the editor synchronously, so
      // the keydown's default action would then run against the contenteditable
      // and insert a paragraph break on top of applying the link.
      event.preventDefault();
      event.stopPropagation();
      this.confirmLink();
      return;
    }

    if (event.key === 'Escape') {
      // Several call sites render inside a native <dialog>; an unhandled
      // Escape would dismiss the whole modal rather than just this bubble.
      event.preventDefault();
      event.stopPropagation();
      this.closeLinkBubble();
    }
  }

  removeCurrentLink(): void {
    const range = this.reclaimSelection();
    if (!range) {
      return;
    }
    removeLink(this.root, range);

    this.linkBubbleOpen = false;
    this.linkInvalid = false;
    this.cdr.markForCheck();
    this.process('command');
  }

  closeLinkBubble(restoreCaret = true): void {
    if (!this.linkBubbleOpen) {
      return;
    }
    this.linkBubbleOpen = false;
    this.linkInvalid = false;
    if (restoreCaret) {
      this.reclaimSelection();
    }
    this.cdr.markForCheck();
  }

  /**
   * A pointer press anywhere outside the bubble dismisses it. The caret is
   * deliberately not restored here -- the click the author just made is where
   * they want to be.
   */
  private onDocumentPointerDown(event: Event): void {
    if (!this.linkBubbleOpen) {
      return;
    }

    const target = event.target;
    const element = target instanceof Element
      ? target
      : (target instanceof Node ? target.parentElement : null);
    if (!element) {
      return;
    }

    if (this.linkBubbleRef?.nativeElement.contains(element)) {
      return;
    }
    // The link button handles its own toggle; let it, or it would reopen.
    if (element.closest('[data-rte-link-toggle]')) {
      return;
    }

    this.zone.run(() => this.closeLinkBubble(false));
  }

  /**
   * Returns focus and the caret to the editor, at the position the link bubble
   * was opened against, and hands back the resulting range.
   */
  private reclaimSelection(): Range | null {
    this.root.focus();
    bookmark.restore(this.root, this.linkTarget ?? this.lastSelection);
    return this.currentRange();
  }

  /** Keeps the caret where it is when a toolbar button is clicked. */
  preserveSelection(event: Event): void {
    event.preventDefault();
  }

  /** Roving tabindex: the toolbar is a single tab stop. */
  onToolbarKeydown(event: KeyboardEvent, index: number, total: number): void {
    let next = index;
    if (event.key === 'ArrowRight') {
      next = (index + 1) % total;
    } else if (event.key === 'ArrowLeft') {
      next = (index - 1 + total) % total;
    } else if (event.key === 'Home') {
      next = 0;
    } else if (event.key === 'End') {
      next = total - 1;
    } else {
      return;
    }

    event.preventDefault();
    this.activeButton = next;
    const buttons = this.host.nativeElement.querySelectorAll<HTMLButtonElement>('.brt-rte__button');
    buttons.item(next)?.focus();
  }

  // --- Event handling -------------------------------------------------------

  private onBeforeInput(event: InputEvent): void {
    if (this.disabled || this.readonly) {
      event.preventDefault();
      return;
    }

    const type = event.inputType;

    if (type === 'historyUndo' || type === 'historyRedo') {
      event.preventDefault();
      this.applyHistory(type === 'historyUndo' ? 'undo' : 'redo');
      return;
    }

    // Cmd+U and the OS format menus would otherwise inject tags we cannot store.
    if (FORBIDDEN_INPUT_TYPES.has(type)) {
      event.preventDefault();
      return;
    }

    if (type === 'formatBold') {
      event.preventDefault();
      this.run('bold');
      return;
    }

    if (type === 'formatItalic') {
      event.preventDefault();
      this.run('italic');
      return;
    }

    if (type === 'insertParagraph') {
      const range = this.currentRange();
      const emptyItem = range ? isEmptyListItem(range.startContainer, this.root) : null;
      if (emptyItem) {
        // Enter in an empty bullet leaves the list instead of adding another.
        event.preventDefault();
        outdentEmptyItem(this.root, emptyItem);
        this.process('command');
        return;
      }
    }
  }

  private onInput(event: InputEvent): void {
    if (this.isComposing || this.suppressProcessing) {
      return;
    }

    if (event.data === ' ') {
      this.tryAutolink();
    }

    const kind: HistoryKind = event.inputType.startsWith('delete') ? 'delete' : 'type';
    if (event.data === ' ' || event.data === null) {
      // Word boundaries end the undo run, so one Cmd+Z removes a word.
      this.history.checkpoint();
    }

    this.process(kind);
  }

  private onKeyDown(event: KeyboardEvent): void {
    const meta = event.metaKey || event.ctrlKey;
    if (!meta) {
      return;
    }

    const key = event.key.toLowerCase();

    if (key === 'v' && event.shiftKey) {
      this.forcePlainTextPaste = true;
      return;
    }

    if (key === 'z') {
      // Firefox does not always emit the history input types.
      event.preventDefault();
      this.applyHistory(event.shiftKey ? 'redo' : 'undo');
      return;
    }

    if (key === 'y') {
      event.preventDefault();
      this.applyHistory('redo');
      return;
    }

    if (key === 'k') {
      event.preventDefault();
      this.zone.run(() => this.openLinkBubble());
      return;
    }

    if (event.key === '\\') {
      event.preventDefault();
      this.zone.run(() => this.run('clear'));
    }
  }

  private onPaste(event: ClipboardEvent): void {
    if (this.disabled || this.readonly) {
      return;
    }
    event.preventDefault();

    const clipboard = event.clipboardData;
    if (!clipboard) {
      return;
    }

    const html = clipboard.getData('text/html');
    const plain = clipboard.getData('text/plain');
    const fragment = this.forcePlainTextPaste || !html
      ? plainTextToFragment(plain)
      : parseExternalHtml(html);
    this.forcePlainTextPaste = false;

    const range = this.currentRange();
    if (!range) {
      return;
    }

    range.deleteContents();
    const lastNode = fragment.lastChild;
    range.insertNode(fragment);

    if (lastNode) {
      const selection = window.getSelection();
      const after = document.createRange();
      after.selectNodeContents(lastNode);
      after.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(after);
    }

    this.history.checkpoint();
    this.process('command');
  }

  private onFocus(): void {
    this.isFocused = true;
  }

  private onBlur(): void {
    this.isFocused = false;
    this.history.checkpoint();

    if (this.pendingValue !== null) {
      const queued = this.pendingValue;
      this.pendingValue = null;
      this.applyValue(queued);
    }

    this.zone.run(() => {
      this.onTouchedFn();
      this.blurred.emit();
    });
  }

  private onSelectionChange(): void {
    const saved = bookmark.save(this.root);
    if (saved) {
      this.lastSelection = saved;
    }
    this.scheduleStateRefresh();
  }

  // --- Internals ------------------------------------------------------------

  private on(target: EventTarget, type: string, handler: (event: Event) => void): void {
    target.addEventListener(type, handler);
    this.listeners.push(() => target.removeEventListener(type, handler));
  }

  private currentRange(): Range | null {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return null;
    }
    const range = selection.getRangeAt(0);
    return this.root.contains(range.commonAncestorContainer) ? range : null;
  }

  private applyValue(html: string): void {
    this.suppressProcessing = true;
    this.root.replaceChildren(...Array.from(parseValue(html).childNodes));
    this.suppressProcessing = false;

    this.lastEmitted = serialize(this.root);
    this.history.seed(this.root.innerHTML, null);
    this.refreshState();
  }

  private applyHistory(direction: 'undo' | 'redo'): void {
    const entry = direction === 'undo' ? this.history.undo() : this.history.redo();
    if (!entry) {
      return;
    }

    this.suppressProcessing = true;
    this.root.innerHTML = entry.html;
    bookmark.restore(this.root, entry.bookmark);
    this.suppressProcessing = false;

    this.emitIfChanged();
    this.refreshState();
  }

  /** Links the URL the author just finished typing, keeping the caret after it. */
  private tryAutolink(): void {
    const selection = window.getSelection();
    const node = selection?.anchorNode;
    if (!selection || !node || node.nodeType !== Node.TEXT_NODE) {
      return;
    }

    const offset = selection.anchorOffset;
    if (offset < 2) {
      return;
    }

    const trailing = autolinkBefore(this.root, node as Text, offset - 1);
    if (!trailing) {
      return;
    }

    const range = document.createRange();
    range.setStart(trailing, Math.min(1, trailing.data.length));
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  private process(kind: HistoryKind): void {
    if (this.suppressProcessing) {
      return;
    }

    normalize(this.root);
    this.history.push(this.root.innerHTML, bookmark.save(this.root), kind);
    this.emitIfChanged();
    this.scheduleStateRefresh();
  }

  private emitIfChanged(): void {
    const value = serialize(this.root);
    if (value === this.lastEmitted) {
      return;
    }
    this.lastEmitted = value;

    // Consumers debounce this into a GraphQL mutation, so it has to run inside
    // the zone even though the listener that produced it does not.
    this.zone.run(() => {
      this.onChangeFn(value);
      this.changed.emit(value);
    });
  }

  private scheduleStateRefresh(): void {
    if (this.frame) {
      return;
    }
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.refreshState();
    });
  }

  private refreshState(): void {
    const range = this.currentRange();
    const value = serialize(this.root);

    const next: ToolbarState = {
      bold: range ? hasMarkThroughout(this.root, range, 'B') : false,
      italic: range ? hasMarkThroughout(this.root, range, 'I') : false,
      ul: range ? activeList(this.root, range) === 'UL' : false,
      ol: range ? activeList(this.root, range) === 'OL' : false,
      link: range ? linkAt(this.root, range.startContainer) !== null : false,
      canUndo: this.history.canUndo,
      canRedo: this.history.canRedo,
      empty: value.length === 0,
    };

    const unchanged = (Object.keys(next) as Array<keyof ToolbarState>)
      .every((key) => next[key] === this.state[key]);
    if (unchanged) {
      return;
    }

    this.zone.run(() => {
      this.state = next;
      this.root.toggleAttribute('data-empty', next.empty);
      this.cdr.markForCheck();
    });
  }

  private openLinkBubble(): void {
    if (this.disabled || this.readonly) {
      return;
    }

    // The toolbar cancels mousedown, so the caret is normally still in the
    // editor. If the editor was never focused, fall back to the end of it.
    let range = this.currentRange();
    if (!range) {
      this.root.focus();
      bookmark.restore(this.root, this.lastSelection ?? bookmark.collapsed(Number.MAX_SAFE_INTEGER));
      range = this.currentRange();
    }
    if (!range) {
      return;
    }

    this.linkTarget = bookmark.save(this.root);

    const existing = linkAt(this.root, range.startContainer);
    this.linkDraft = existing?.getAttribute('href') ?? '';
    this.linkBubbleEditing = existing !== null;
    this.linkInvalid = false;

    const anchorRect = (existing ?? range).getBoundingClientRect();
    const hostRect = this.root.getBoundingClientRect();
    this.linkBubbleTop = anchorRect.bottom - hostRect.top + 8;
    this.linkBubbleLeft = Math.max(0, anchorRect.left - hostRect.left);

    this.linkBubbleOpen = true;
    this.cdr.markForCheck();

    window.setTimeout(() => {
      const input = this.linkInputRef?.nativeElement;
      input?.focus();
      input?.select();
    }, 0);
  }
}
